import React, { useMemo } from 'react';

const FunnelChart = ({ appAnalytics, utmAnalytics, webAdEvents, authProviderAnalytics }) => {
    const funnelData = useMemo(() => {
        if (!appAnalytics || appAnalytics.length === 0) {
            return [];
        }

        // Calculate summations for new stages
        const installAttributionUsers = utmAnalytics?.reduce((sum, item) => sum + (item.uniqueUsers || 0), 0) || 0;

        const loginModalUsers = (
            (appAnalytics.find(e => e.type === 'loginModalViaBtn')?.uniqueUsers || 0) +
            (appAnalytics.find(e => e.type === 'loginModalViaContest')?.uniqueUsers || 0)
        );

        const signupAuthProvidersUsers = authProviderAnalytics?.reduce((sum, item) => sum + (item.totalUsers || 0), 0) || 0;

        // Define the complete funnel stages in order
        const stages = [
            {
                name: 'Install Attribution',
                eventType: 'installAttribution',
                isCustom: true,
                value: installAttributionUsers,
                description: 'UTM Campaign Installs'
            },
            {
                name: 'First Open',
                eventType: 'firstOpen',
                isCustom: false
            },
            {
                name: 'Onboard Screen 1',
                eventType: 'onBoardScreen1BtnPress',
                isCustom: false
            },
            {
                name: 'Onboard Screen 2',
                eventType: 'onBoardScreen2BtnPress',
                isCustom: false
            },
            {
                name: 'Onboard Screen 3',
                eventType: 'onBoardScreen3BtnPress',
                isCustom: false
            },
            {
                name: 'Onboard Screen 4',
                eventType: 'onBoardScreen4BtnPress',
                isCustom: false
            },
            {
                name: 'Login Modal',
                eventType: 'loginModal',
                isCustom: true,
                value: loginModalUsers,
                description: 'Button + Contest Logins'
            },
            {
                name: 'Auth Provider Signup',
                eventType: 'authProviderSignup',
                isCustom: true,
                value: signupAuthProvidersUsers,
                description: 'Google + Password Signups'
            },
        ];

        // Extract unique users for each stage
        const data = stages.map((stage) => {
            let users = 0;
            if (stage.isCustom) {
                users = stage.value;
            } else {
                const event = appAnalytics.find((e) => e.type === stage.eventType);
                users = event?.uniqueUsers || 0;
            }

            return {
                name: stage.name,
                users,
                total: 0,
                eventType: stage.eventType,
                description: stage.description,
                isCustom: stage.isCustom,
            };
        });

        // Calculate conversion rates
        const baselineUsers = data[0]?.users || 1;
        return data.map((stage, idx) => ({
            ...stage,
            percentage: ((stage.users / baselineUsers) * 100).toFixed(2),
            conversionRate:
                idx === 0
                    ? '100%'
                    : (((stage.users / data[idx - 1].users) * 100).toFixed(2) + '%'),
        }));
    }, [appAnalytics, utmAnalytics, webAdEvents, authProviderAnalytics]);

    if (funnelData.length === 0) {
        return null;
    }

    const maxWidth = 500;
    const containerHeight = 600;
    const segmentHeight = containerHeight / funnelData.length;
    const maxUsers = funnelData[0].users;

    return (
        <div className="mb-8 p-4">
            <h3 className="text-lg font-semibold mb-6">📊 Complete User Funnel</h3>

            <div className="flex gap-12">
                {/* Funnel Visualization */}
                <div className="flex-1">
                    <svg width="100%" height={containerHeight} viewBox={`0 0 ${maxWidth} ${containerHeight}`}>
                        {funnelData.map((stage, idx) => {
                            const percentage = (stage.users / maxUsers) * 100;
                            const width = (maxWidth * percentage) / 100;
                            const x = (maxWidth - width) / 2;
                            const y = idx * segmentHeight;

                            // Calculate next stage width for trapezoid
                            const nextPercentage =
                                idx < funnelData.length - 1
                                    ? (funnelData[idx + 1].users / maxUsers) * 100
                                    : percentage;
                            const nextWidth = (maxWidth * nextPercentage) / 100;
                            const nextX = (maxWidth - nextWidth) / 2;

                            // Color gradient
                            const colors = [
                                '#8b5cf6', // Purple - Install
                                '#10b981', // Green - First Open
                                '#3b82f6', // Blue - Screen 1
                                '#f59e0b', // Amber - Screen 2
                                '#ef4444', // Red - Screen 3
                                '#ec4899', // Pink - Screen 4
                                '#06b6d4', // Cyan - Login
                                '#14b8a6', // Teal - Auth Signup
                            ];

                            return (
                                <g key={stage.eventType}>
                                    {/* Trapezoid */}
                                    <polygon
                                        points={`${x},${y} ${x + width},${y} ${nextX + nextWidth},${y + segmentHeight} ${nextX},${y + segmentHeight}`}
                                        fill={colors[idx]}
                                        fillOpacity="0.8"
                                        stroke={colors[idx]}
                                        strokeWidth="1"
                                    />

                                    {/* Stage Label */}
                                    <text
                                        x={maxWidth / 2}
                                        y={y + segmentHeight / 2 - 10}
                                        textAnchor="middle"
                                        fontSize="16"
                                        fontWeight="bold"
                                        fill="white"
                                        pointerEvents="none"
                                    >
                                        {stage.name}
                                    </text>

                                    {/* User Count */}
                                    <text
                                        x={maxWidth / 2}
                                        y={y + segmentHeight / 2 + 8}
                                        textAnchor="middle"
                                        fontSize="18"
                                        fontWeight="bold"
                                        fill="white"
                                        pointerEvents="none"
                                    >
                                        {stage.users} users
                                    </text>

                                    {/* Description for custom stages */}
                                    {stage.description && (
                                        <text
                                            x={maxWidth / 2}
                                            y={y + segmentHeight / 2 + 24}
                                            textAnchor="middle"
                                            fontSize="13"
                                            fill="white"
                                            pointerEvents="none"
                                            opacity="0.9"
                                        >
                                            {stage.description}
                                        </text>
                                    )}
                                </g>
                            );
                        })}
                    </svg>
                </div>

                {/* Stats Table */}
                <div className="flex-1">
                    <table className="w-full border text-sm mb-4" style={{ fontSize: '11px' }}>
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="p-2 border text-left">Stage</th>
                                <th className="p-2 border text-right">Users</th>
                                <th className="p-2 border text-right">Drop</th>
                                <th className="p-2 border text-right">Conv.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {funnelData.map((stage, idx) => {
                                const dropOff =
                                    idx === 0 ? 0 : funnelData[idx - 1].users - stage.users;
                                return (
                                    <tr key={stage.eventType}>
                                        <td className="p-2 border text-sm font-medium">
                                            {stage.name}
                                        </td>
                                        <td className="p-2 border text-right font-semibold">
                                            {stage.users}
                                        </td>
                                        <td className="p-2 border text-right text-red-600">
                                            {dropOff > 0 ? `-${dropOff}` : '-'}
                                        </td>
                                        <td className="p-2 border text-right font-semibold">
                                            {stage.conversionRate}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {/* Summary Stats */}
                    <div className="bg-gray-50 p-4 rounded border text-sm">
                        <p className="mb-2">
                            <strong>Total Dropoff:</strong>{' '}
                            {funnelData[0].users - funnelData[funnelData.length - 1].users}{' '}
                            users
                        </p>
                        <p className="mb-2">
                            <strong>Overall Conversion:</strong>{' '}
                            {(
                                (funnelData[funnelData.length - 1].users / funnelData[0].users) *
                                100
                            ).toFixed(2)}
                            %
                        </p>
                        <p className="text-xs text-gray-600 mt-3">
                            Funnel shows user journey from install attribution through signup
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FunnelChart;
